import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  Loader,
  Text,
  Grid,
  Box,
  Alert,
  Flex,
  Modal,
  Button,
  Divider,
  Group,
} from "@mantine/core";
import axios from "axios";
import toast from "react-hot-toast";

import { UserGroupForm } from "./";
import { UpdateButton } from "@components/shared/UserAdd/UserGroup";
import { UserGroupUseList } from "./";
import entityIcon from "@util/entityIcon";
import { SnapshotQueryVisualizer } from "@components/ReportRecipe";

export default function UserGroupDetail({
  id,
  info,
  fetchData,
  editable = false,
}) {
  const queryClient = useQueryClient();

  const {
    data: userGroup,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["user-group", id],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`/user-groups/${id}/`);
        return data.response[0];
      } catch (err) {
        throw err;
      }
    },
    retry: 1,
    retryDelay: 1000,
  });

  const fetchUserGroup = () =>
    queryClient.invalidateQueries({ queryKey: ["user-group", id] });

  // Handle error state
  if (error) {
    return (
      <div style={{ minHeight: "100px" }}>
        <Alert color="red" title="Error loading segment">
          {error || "Failed to load segment details"}
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100px" }}>
      {(isLoading || isFetching) && <Loader size="xs" color="teal" />}
      {userGroup && (
        <div>
          <Grid gutter="xl">
            {editable && (
              <Grid.Col span={{ base: 12, md: 5 }}>
                <Box
                  style={{
                    border: "1px solid var(--mantine-color-gray-2)",
                    borderRadius: "8px",
                    backgroundColor: "var(--mantine-color-gray-0)",
                    padding: "var(--mantine-spacing-xs)",
                  }}
                >
                  <UserGroupForm
                    info={userGroup}
                    id={id}
                    onSuccess={() => {
                      fetchUserGroup();
                      fetchData();
                    }}
                  />
                </Box>
              </Grid.Col>
            )}
            <Grid.Col span={{ base: 12, md: editable ? 7 : 12 }}>
              <Box
                style={{
                  border: "1px solid var(--mantine-color-gray-2)",
                  borderRadius: "8px",
                  backgroundColor: "var(--mantine-color-gray-0)",
                  padding: "var(--mantine-spacing-xs)",
                }}
              >
                <Text fw={500} mb="xs" size="sm">
                  Where's this group being used?
                </Text>
                <UserGroupUseList
                  userGroupId={id}
                  useItems={userGroup.use_cases}
                />
              </Box>
              {editable && (
                <>
                  {userGroup.updateable ? (
                    <Flex
                      gap="sm"
                      mt="lg"
                      justify={{ base: "flex-start", md: "flex-end" }}
                      align="center"
                    >
                      <UpdateButton
                        item={{
                          ...userGroup,
                          user_group_id: id,
                        }}
                        fetchData={fetchUserGroup}
                      />
                      <Text size="sm" fw={500}>
                        Update the users in this group
                      </Text>
                    </Flex>
                  ) : (
                    <Text size="sm" fw={500} align="right" mt="lg" c="gray">
                      This group is not updateable
                    </Text>
                  )}
                </>
              )}
            </Grid.Col>
          </Grid>
          {userGroup.snapshot_query_visualizer_data && (
            <>
              <Divider my="md" />
              <Box>
                <Flex gap="xs" align="center">
                  <>{entityIcon.snapshot(16)}</>
                  <Text size="sm" fw={600}>
                    {userGroup.snapshot_title}
                  </Text>
                </Flex>
                <SnapshotQueryVisualizer
                  data={userGroup.snapshot_query_visualizer_data}
                />
              </Box>
            </>
          )}
          {editable && (
            <>
              <Divider my="md" />
              <Box mt="md">
                <DeleteUserGroup id={id} onSuccess={fetchData} />
              </Box>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DeleteUserGroup({ id, onSuccess }) {
  const [opened, setOpened] = useState(false);

  function onDelete() {
    return axios.delete(`/user-groups/${id}/`);
  }

  const mutation = useMutation({
    mutationFn: onDelete,
    onSuccess: () => {
      onSuccess();
      setOpened(false);
      toast.success("Segment deleted");
    },
    onError: (err) => {
      toast.error(err);
    },
  });

  return (
    <>
      <Modal
        opened={opened}
        onClose={() => {
          if (mutation.isPending) return;
          setOpened(false);
        }}
        title="Delete Segment"
        centered
      >
        <Text>Are you sure you want to delete this segment?</Text>
        <Button
          color="red"
          onClick={() => mutation.mutate()}
          loading={mutation.isPending}
          fullWidth
          mt="md"
        >
          Delete
        </Button>
      </Modal>
      <Button
        color="gray"
        onClick={() => setOpened(true)}
        leftSection={entityIcon.trash(16)}
        size="xs"
        variant="subtle"
      >
        Delete Segment
      </Button>
    </>
  );
}
